import { Day, Itinerary, Stop } from "@city-sage/core-client";
import { Calendar, MapPin, Edit3, Crown } from "lucide-react";
import { Badge, Button, Card, useRepository } from "@city-sage/aurum-avis-labs-components";
import { useReactions } from "../../lib/useReactions";
import { LikeButton } from "../actions/LikeButton";
import { CommentButton } from "../actions/CommentButton";
import { ShareButton } from "../actions/ShareButton";
import { motion } from "framer-motion";
import { MarkdownRenderer } from "../MarkdownRenderer";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useIsAuthenticated } from "@azure/msal-react";

interface ItineraryOverviewSectionProps {
  itinerary: Itinerary | undefined
  days: Day[] | undefined
  stops: Stop[] | undefined
}

export const ItineraryOverviewSection = ({ itinerary, days, stops }: ItineraryOverviewSectionProps) => {
  const { api } = useRepository();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { isLiked, likeCount, toggleLike, isPending } = useReactions({
    reactableType: 'itinerary',
    reactableId: itinerary?.id || ''
  });

  // Get current user's account to check ownership
  const { data: currentAccount } = useQuery({
    queryKey: ['account'],
    queryFn: () => api.getMyAccount(),
    enabled: !!isAuthenticated
  });

  // Check if current user owns this itinerary
  const isOwner = currentAccount?.id === itinerary?.account?.id;

  const handleEditClick = () => {
    if (itinerary?.id) {
      navigate({ 
        to: '/itineraries/$itineraryId/edit', 
        params: { itineraryId: itinerary.id } 
      });
    }
  };

  return (
    <div className="h-full flex items-center justify-center px-4 bg-white">
      <div className="max-w-md w-full">
         {/* Owner Quick Actions Card */}
         {isOwner && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
          >
            <Card className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Crown className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900">Your Itinerary</h3>
                    <p className="text-xs text-blue-700">Manage your travel plan</p>
                  </div>
                </div>
                <Button onClick={handleEditClick} size="sm" className="bg-blue-600 hover:bg-blue-700 h-9 px-4">
                  <Edit3 className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </div>
            </Card>
          </motion.div>
        )}
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center space-y-4"
        >
          {/* Title and Description */}
          <div className="space-y-3 relative">
            <h1 className="text-2xl md:text-3xl font-bold leading-tight text-gray-900">
              {itinerary?.title}
            </h1>
            <div className={`text-gray-600 leading-relaxed max-w-sm mx-auto ${itinerary?.description ? "line-clamp-3" : ""}`}>
              <MarkdownRenderer content={itinerary?.shortDescription || itinerary?.description || ''} className="text-gray-600" />
            </div>
          </div>

          {/* Action Buttons - Horizontal Row */}
          <div className="flex justify-center items-center gap-6">
            {/* Like Button */}
            <LikeButton
              isLiked={isLiked}
              likeCount={likeCount}
              onToggleLike={toggleLike}
              isPending={isPending}
              variant="light"
              layout="horizontal"
              size="md"
            />

            {/* Comment Button */}
            <CommentButton
              postableType="itinerary"
              postableId={itinerary?.id || ''}
              variant="light"
              layout="horizontal"
              size="md"
            />

            {/* Share Button */}
            <ShareButton
              url={`${window.location.origin}/itineraries/${itinerary?.id || ''}`}
              title={itinerary?.title || 'CitySage Itinerary'}
              description={itinerary?.shortDescription || itinerary?.description || 'Check out this amazing travel itinerary!'}
              hashtags={['CitySage', 'Travel', 'Itinerary', itinerary?.title?.replace(/\s+/g, '') || 'Adventure']}
              variant="light"
              layout="horizontal"
              size="md"
              shareMethod="dialog"
            />
          </div>

          {/* Stats as Badges */}
          <div className="flex justify-center gap-3">
            <Badge variant="secondary" className="px-3 py-1 text-sm font-medium">
              <Calendar className="h-4 w-4 mr-1.5" />
              {days?.length || 0} Days
            </Badge>
            <Badge variant="secondary" className="px-3 py-1 text-sm font-medium">
              <MapPin className="h-4 w-4 mr-1.5" />
              {stops?.length || 0} Stops
            </Badge>
          </div>
        </motion.div>
      </div>
    </div>
  )
} 